import { useEffect, useState } from "react";
import { Copy, LogOut, Timer, Trash2 } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { clearSession, removeVisited } from "@/lib/storage";
import { deleteRoom } from "@/lib/api";

export default function HUD({ room, self }) {
  const nav = useNavigate();
  const board = room.board;
  const turn = board.turn;
  const [elapsed, setElapsed] = useState(0);
  const [busy, setBusy] = useState(false);
  const timerMax = room.options?.timer_seconds || 0;

  useEffect(() => {
    if (!room.turn_started_at) return;
    const start = new Date(room.turn_started_at).getTime();
    const tick = () => setElapsed(Math.floor((Date.now() - start) / 1000));
    tick();
    const id = setInterval(tick, 500);
    return () => clearInterval(id);
  }, [room.turn_started_at]);

  const copyCode = async () => { try { await navigator.clipboard.writeText(room.code); toast.success("Código copiado"); } catch {} };
  const leave = () => { clearSession(room.code); nav("/"); };
  const onDeleteRoom = async () => {
    if (!self.is_host) return;
    if (!window.confirm(`¿Eliminar la sala ${room.code}? Los jugadores serán expulsados.`)) return;
    setBusy(true);
    try {
      await deleteRoom(room.code, self.id);
      removeVisited(room.code);
      clearSession(room.code);
      toast.success("Sala eliminada");
      nav("/");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "No se pudo eliminar");
    } finally { setBusy(false); }
  };

  const remaining = timerMax > 0 ? Math.max(0, timerMax - elapsed) : null;

  return (
    <div className="mb-4">
      <div className="flex items-center justify-between mb-3">
        <button data-testid="btn-leave-ingame" className="inline-flex items-center gap-2 text-[color:var(--text-dim)] hover:text-white" onClick={leave}>
          <LogOut size={14} /><span className="label-classified">Salir</span>
        </button>
        <div className="flex items-center gap-3">
          {self.is_host && (
            <button data-testid="btn-delete-room-game" onClick={onDeleteRoom} disabled={busy} className="inline-flex items-center gap-1 text-[color:var(--text-dim)] hover:text-[color:var(--red)]">
              <Trash2 size={14} /><span className="label-classified">Eliminar</span>
            </button>
          )}
          <button onClick={copyCode} data-testid="btn-copy-code-hud" className="label-classified inline-flex items-center gap-1 hover:text-white">
            {room.code} <Copy size={12} />
          </button>
        </div>
      </div>

      <div className="grid grid-cols-[1fr_auto_1fr] items-stretch gap-2">
        <TeamScore
          team="red"
          remaining={board.red_remaining}
          active={turn === "red"}
          label="Rojo"
          mine={self.team === "red"}
        />
        <div className="flex flex-col items-center justify-center px-3 py-2 border border-[color:var(--border)] bg-[color:var(--surface)]">
          <div className="label-classified">{board.phase === "clue" ? "Esperando pista" : board.phase === "guess" ? "Adivinando" : "Fin"}</div>
          {remaining !== null && (
            <div className={"font-heading text-2xl inline-flex items-center gap-1 " + (remaining <= 10 ? "text-red-400" : "text-white")}>
              <Timer size={16} /> {remaining}s
            </div>
          )}
          {self.role && (
            <div className="label-classified mt-1">
              {self.role === "spymaster" ? "Jefe de Espías" : "Agente"}{self.team ? ` · ${self.team === "red" ? "Rojo" : "Azul"}` : ""}
            </div>
          )}
        </div>
        <TeamScore
          team="blue"
          remaining={board.blue_remaining}
          active={turn === "blue"}
          label="Azul"
          mine={self.team === "blue"}
        />
      </div>

      {board.clue && board.phase === "guess" && (
        <div className="mt-3 border-2 border-white/10 bg-[color:var(--surface)] p-3 text-center">
          <div className="label-classified mb-1">{board.clue.sudden_death ? "Muerte súbita" : `Pista ${turn === "red" ? "Rojo" : "Azul"}`}</div>
          <div className="font-heading text-3xl tracking-widest uppercase">
            {board.clue.voice ? <span className="text-[color:var(--amber)]">VERBAL</span> : board.clue.word}
            {" "}<span className="text-[color:var(--amber)]">· {board.clue.unlimited ? "∞" : board.clue.number}</span>
          </div>
          <div className="label-classified mt-1">
            Intentos restantes: <span className="text-white">{board.clue.unlimited || board.clue.number === 0 ? "∞" : board.clue.attempts_left}</span>
          </div>
        </div>
      )}

      {/* Variant badges */}
      {(room.options?.killer_waits || room.options?.voice_mode || (board.sudden_death_teams || []).length > 0) && (
        <div className="mt-2 flex flex-wrap gap-2">
          {room.options?.killer_waits && (
            <span className="px-2 py-1 border border-[color:var(--red)] text-[10px] tracking-widest uppercase font-mono text-[color:var(--red)]">
              ASESINO TE ESPERA
            </span>
          )}
          {room.options?.voice_mode && (
            <span className="px-2 py-1 border border-[color:var(--amber)] text-[10px] tracking-widest uppercase font-mono text-[color:var(--amber)]">
              PISTAS EN VOZ
            </span>
          )}
          {(board.sudden_death_teams || []).map((t) => (
            <span key={t} data-testid={`badge-sudden-death-${t}`} className="px-2 py-1 border-2 text-[10px] tracking-widest uppercase font-mono pulse-soft" style={{ borderColor: t === "red" ? "var(--red)" : "var(--blue)", color: t === "red" ? "var(--red)" : "var(--blue)" }}>
              MUERTE SÚBITA · {t === "red" ? "ROJO" : "AZUL"}
            </span>
          ))}
        </div>
      )}
    </div>
  );
}

const TeamScore = ({ team, remaining, active, label, mine }) => {
  const color = team === "red" ? "var(--red)" : "var(--blue)";
  return (
    <div
      data-testid={`score-${team}`}
      className={"relative border-2 px-3 py-2 flex flex-col items-center justify-center " + (active ? "pulse-soft" : "")}
      style={{ borderColor: color, background: active ? `${color}22` : "transparent" }}
    >
      <div className="label-classified" style={{ color }}>{label}{mine ? " (tu equipo)" : ""}{active ? " · turno" : ""}</div>
      <div className="font-heading text-4xl" style={{ color }}>{remaining}</div>
    </div>
  );
};
